import { useCallback, useContext } from 'react';
import Promise from 'bluebird';
import {
  loadBundleCountries,
  loadCampaign,
  modelToViewModelForCampaign,
} from '@bingads-webui-campaign-react/perf-max-step-wizard';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { useWizardService, WIZARD_EVENTS } from '@bingads-webui-react/wizard';
import { ScenarioContext, ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  readLocationCriterions,
} from '@bingads-webui-campaign-react/perf-max-data';
import { saveCampaign, saveAssetGroup } from '@bingads-webui-campaign-react/unified-app-shared';

export const useTargetingStepRepository = () => {
  const serverContext = useContext(ServerContext);
  const scenarioContext = useContext(ScenarioContext);
  const { scope } = useContext(ScopeContext) || {};

  const { cid, aid } = scope.values;

  const { state, send } = useWizardService();

  const activity = useContext(ActivityContext);

  const i18n = useContext(I18nContext);

  const load = useCallback(() => {
    const savedCampaign = state.context.wizardData.campaign;
    const savedAssetGroup = state.context.wizardData.assetGroup;
    const { Id: campaignId } = savedCampaign || {};

    const loadPromises = {
      campaign: loadCampaign(cid, aid, campaignId),
      locationTargets: readLocationCriterions(cid, aid, campaignId),
      assetGroup: Promise.resolve(savedAssetGroup),
      bundleCountriesAndLocationCriterions: loadBundleCountries({
        cid,
        aid,
        serverContext,
      }),
    };

    return Promise.props(loadPromises).then(({
      bundleCountriesAndLocationCriterions,
      campaign,
      assetGroup,
      locationTargets,
    }) => {
      const campaignData = modelToViewModelForCampaign({
        campaignModel: { ...campaign, Status: savedCampaign.status }, // MT sends UserPaused, if we send this back they mark it as active. So we just use Paused from the saved ad model
        locationCriterionsModel: locationTargets ? locationTargets.value : [],
        bundleCountries: bundleCountriesAndLocationCriterions.bundleCountries,
      });

      return ({
        ...bundleCountriesAndLocationCriterions,
        campaign: campaignData.data,
        assetGroup,
      });
    });
  }, [aid, cid, serverContext, state.context.wizardData.assetGroup, state.context.wizardData.campaign]);

  const save = useCallback(({
    data,
    initialData,
  }) => {
    const {
      campaign: campaignToSave,
      assetGroup: assetGroupToSave,
      bundleCountries,
      bundleLocationCriterions,
    } = data || {};

    const { assetGroup: initialAssetGroup } = initialData;

    const savePromises = {
      campaign: saveCampaign({
        campaign: { data: campaignToSave },
        bundleCountries,
        bundleLocationCriterions,
      }, {
        scenario: scenarioContext.name,
        i18n,
        activityLogger: scenarioContext.createActivity('updateCampaign'),
      }),
      assetGroup: saveAssetGroup({
        assetGroup: { data: assetGroupToSave },
        originalAssetGroup: { data: initialAssetGroup },
        campaign: campaignToSave,
      }, {
        scenario: scenarioContext.name,
        i18n,
        activityLogger: scenarioContext.createActivity('updateAssetGroup'),
        serverContext,
        cid,
        aid,
        activity,
      }),
    };

    return Promise.props(savePromises).then(({
      campaign: campaignResults,
      assetGroup: assetGroupResults,
    }) => {
      activity.trace('[UnifiedAppWizard] - campaign/assetgroup settings updated in targeting step');
      activity.signal('[UnifiedAppWizard] - campaign/assetgroup settings updated in targeting step');

      const {
        campaign,
      } = campaignResults;

      const {
        assetGroup,
      } = assetGroupResults;

      send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { campaign: campaign.data });
      send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { assetGroup: assetGroup.data });

      return {
        campaign: campaign.data,
        assetGroup: assetGroup.data,
      };
    }).catch((errorData) => {
      activity.trace('[UnifiedAppWizard] - campaign/assetgroup settings update failed in targeting step');

      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        error: {
          ...errorData,
        },
      });
    });
  }, [activity, aid, cid, i18n, scenarioContext, send, serverContext]);

  return {
    load,
    save,
  };
};
